import type { CacheStore } from "@ease/infra/cache";

export interface CascadeRateCounter {
  /** Increment and return current count for a tenant in the current window. */
  incrementAndGet(tenantId: string): Promise<number>;
  /** Get current count without incrementing. */
  getCount(tenantId: string): Promise<number>;
}

const WINDOW_SECONDS = 60;
const TTL_SECONDS = WINDOW_SECONDS * 2;

/**
 * KV-backed sliding window counter for per-tenant event rate limiting.
 * Uses minute-bucketed keys: cascade:{tenantId}:{minuteEpoch}
 * Each key has a TTL of 2 minutes (window + buffer).
 */
export function createCascadeRateCounter(store: CacheStore): CascadeRateCounter {
  function bucketKey(tenantId: string): string {
    const minuteEpoch = Math.floor(Date.now() / (WINDOW_SECONDS * 1000));
    return `cascade:${tenantId}:${minuteEpoch}`;
  }

  return {
    // NOTE: KV does not support atomic increment. Concurrent incrementAndGet calls
    // for the same tenant across different DOs may under-count by 1-2. This is
    // acceptable for rate limiting with a 1000/min threshold — the counter is a
    // heuristic, not an exact ledger.
    async incrementAndGet(tenantId: string): Promise<number> {
      const key = bucketKey(tenantId);
      const current = await store.get<number>(key);
      const next = (current ?? 0) + 1;
      await store.set(key, next, TTL_SECONDS);
      return next;
    },

    async getCount(tenantId: string): Promise<number> {
      const key = bucketKey(tenantId);
      return (await store.get<number>(key)) ?? 0;
    },
  };
}
